import { invoke } from '@shared/easyfy.decorators';
import { globalEvent } from '@shared/easyfy.core';
import Easyfy, { EventNames } from '@shared/easyfy.events/easyfy.eventhandler';
import { IEasyfyEvent } from '@shared/easyfy.events/easyfy.eventdata';
import OtpDigitInputs from './otp-digit-inputs';
import { deleteCookie, getCookieByName } from '@clientShared/utils';
import { logCustomMessage } from '@shared/easyfy.logger';
import TranslatorService, { TranslationKeys } from '@clientWebServices/translator.service';
import MessageService from '@clientWebServices/message.service';

/**
 * Handles the multi-step OTP login flow with sliding panels
 */
@invoke
export default class OtpFlow {
  static Instance: OtpFlow;

  private readonly OTP_COOKIE_NAME = 'otp_attempt_id';
  private email: string | null = null;
  private isResend = false;
  private emailInputListenerAttached = false;

  constructor() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'open-otp-login',
          callback: this.openEmailEntryOrResume,
        },
        {
          event: 'otp-back-to-login',
          callback: this.closeEmailEntry,
        },
        {
          event: 'otp-back-to-email',
          callback: this.backToEmailEntry,
        },
        {
          event: 'otp-resend-code',
          callback: this.resendCode,
        },
      ],
    });

    document.addEventListener('form-success', this.handleFormSuccess);
    document.addEventListener('form-error', this.handleFormError);

    this.autoOpenOtpFlowFromUrl();
  }

  private getEmailEntryPanel(): HTMLElement | null {
    return document.querySelector('[data-container="otp-email-entry"]');
  }

  private getCodeEntryPanel(): HTMLElement | null {
    return document.querySelector('[data-container="otp-code-entry"]');
  }

  private openEmailEntryOrResume = (e: Event) => {
    e.preventDefault();

    this.fireLoginEvent({ eventName: EventNames.LoginChooseMethod, data: { loginMethod: 'OTP' } });

    this.openEmailEntry();
  };

  private openEmailEntry = () => {
    const panel = this.getEmailEntryPanel();
    this.showPanel(panel);

    requestAnimationFrame(() => {
      const emailInput = document.getElementById('otpEmailInput') as HTMLInputElement;
      this.attachEmailInputClearErrorListener(emailInput);
      emailInput?.focus();
    });
  };

  private resumeOrStartOtpFlow = async () => {
    if (this.hasOtpCookie()) {
      const result = await this.checkActiveAttempt();
      if (result.hasActiveAttempt && result.email) {
        this.storeEmail(result.email);
        this.showCodeEntry(result.email);
        return;
      }
    }

    this.openEmailEntry();
    const translator = TranslatorService.getInstance();
    const message = await translator.translate('otp_request_new_code');
    requestAnimationFrame(() => {
      this.setValidationSummary('otp-generate-form', message);
    });
  };

  private checkActiveAttempt = async (): Promise<{ hasActiveAttempt: boolean; email?: string }> => {
    try {
      const response = await fetch('/api/checkloginattemptstatus/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return { hasActiveAttempt: false };

      const data = await response.json();
      return {
        hasActiveAttempt: data.hasActiveAttempt === true,
        email: data.email,
      };
    } catch {
      return { hasActiveAttempt: false };
    }
  };

  private closeEmailEntry = async (e: Event) => {
    e.preventDefault();

    const emailPanel = this.getEmailEntryPanel();
    const codePanel = this.getCodeEntryPanel();

    this.clearLoginAttemptCookie();

    this.hidePanel(emailPanel);
    this.hidePanel(codePanel);
  };

  private backToEmailEntry = async (e: Event) => {
    e.preventDefault();

    this.clearLoginAttemptCookie();

    const codePanel = this.getCodeEntryPanel();
    this.hidePanel(codePanel);

    requestAnimationFrame(() => {
      const emailPanel = this.getEmailEntryPanel();
      this.showPanel(emailPanel);
    });
  };

  private resendCode = (e: Event) => {
    e.preventDefault();

    const resendButton = this.getResendButton();
    if (!resendButton || resendButton.disabled) return;

    const email = this.getStoredEmail();
    if (!email) return;

    this.isResend = true;
    this.setButtonProcessingState(resendButton);

    const form = document.querySelector('[data-container="otp-generate-form"]') as HTMLFormElement;
    if (form) {
      const emailInput = document.getElementById('otpEmailInput') as HTMLInputElement;
      if (emailInput) {
        emailInput.value = email;
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  };

  private getResendButton = (): HTMLButtonElement | null => document.querySelector('[data-event="otp-resend-code"]');

  private setButtonProcessingState = (button: HTMLElement) => {
    button.dataset.state = 'processing';
    button.setAttribute('disabled', 'disabled');
    if (!button.querySelector('.state')) {
      button.appendChild(document.createElement('div')).classList.add('state');
    }
  };

  private setButtonSuccessState = (button: HTMLElement) => {
    button.dataset.state = 'success';
  };

  private setButtonFailState = (button: HTMLElement) => {
    button.dataset.state = 'fail';
  };

  private resetButtonState = (button: HTMLElement) => {
    button.removeAttribute('disabled');
    button.removeAttribute('data-state');
    button.querySelector('.state')?.remove();
  };

  private handleResendSuccess = () => {
    const button = this.getResendButton();
    if (!button) return;

    this.setButtonSuccessState(button);
    MessageService.Instance.displaySuccessMessage('otp_code_resent');

    setTimeout(() => {
      this.resetButtonState(button);
      this.startResendCooldown();
    }, 2000);
  };

  private handleResendError = () => {
    const button = this.getResendButton();
    if (!button) return;

    this.setButtonFailState(button);

    setTimeout(() => {
      this.resetButtonState(button);
    }, 2000);
  };

  private onOtpGenerated = (e: Event) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    if (!emailInput) return;

    const email = emailInput.value;

    this.setEmailInputErrorState(false);
    this.fireLoginEvent({ eventName: EventNames.LoginSendOtpCode, data: { loginMethod: 'OTP' } });
    this.storeEmail(email);

    if (this.isResend) {
      this.isResend = false;
      this.handleResendSuccess();
      return;
    }

    const emailPanel = this.getEmailEntryPanel();
    this.hidePanel(emailPanel);

    requestAnimationFrame(() => {
      this.showCodeEntry(email);
    });
  };

  private onOtpValidated = () => {
    this.clearStoredEmail();

    const codePanel = this.getCodeEntryPanel();
    this.hidePanel(codePanel);
  };

  private showCodeEntry = (email: string) => {
    const emailDisplay = document.querySelector('[data-container="otp-display-email"]');
    if (emailDisplay) {
      emailDisplay.textContent = email;
    }

    const emailHiddenInput = document.getElementById('otpEmailValue') as HTMLInputElement;
    if (emailHiddenInput) {
      emailHiddenInput.value = email;
    }

    const codePanel = this.getCodeEntryPanel();
    this.showPanel(codePanel);

    this.startResendCooldown();

    requestAnimationFrame(() => {
      OtpDigitInputs.Instance?.reinitialize();
    });
  };

  private getResendCooldownMs = (): number => {
    const codePanel = this.getCodeEntryPanel();
    const seconds = parseInt(codePanel?.dataset?.resendCooldown || '30', 10);
    return seconds * 1000;
  };

  private startResendCooldown = () => {
    const button = this.getResendButton();
    if (!button) return;

    button.setAttribute('disabled', 'disabled');

    setTimeout(() => {
      button.removeAttribute('disabled');
    }, this.getResendCooldownMs());
  };

  private clearLoginAttemptCookie = (): void => {
    deleteCookie(this.OTP_COOKIE_NAME);
    this.clearStoredEmail();
  };

  private showPanel = (panel: HTMLElement | null) => {
    if (!panel) return;

    panel.setAttribute('data-toggle-open', '');
    panel.style.visibility = 'visible';
    panel.style.display = 'grid';
  };

  private hidePanel = (panel: HTMLElement | null) => {
    if (!panel) return;

    panel.removeAttribute('data-toggle-open');
    panel.style.visibility = 'hidden';
    panel.style.display = 'none';
  };

  private storeEmail = (email: string) => {
    this.email = email;
  };

  private getStoredEmail = (): string | null => {
    return this.email;
  };

  private clearStoredEmail = () => {
    this.email = null;
  };

  private hasOtpCookie = (): boolean => {
    return !!getCookieByName(this.OTP_COOKIE_NAME);
  };

  private fireLoginEvent = (event: IEasyfyEvent) => {
    try {
      Easyfy.EasyfyEventHandler.RunEvent(event);
    } catch (error) {
      logCustomMessage('Failed to fire login event', { eventName: event.eventName, error: String(error) });
    }
  };

  private handleFormSuccess = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target?.dataset?.container === 'otp-generate-form') {
      this.onOtpGenerated(e);
    } else if (target?.dataset?.container === 'otp-validate-form') {
      this.onOtpValidated();
    }
  };

  private attachEmailInputClearErrorListener = (emailInput: HTMLInputElement | null) => {
    if (!emailInput || this.emailInputListenerAttached) return;

    emailInput.addEventListener('input', () => {
      this.setEmailInputErrorState(false);
      this.setValidationSummary('otp-generate-form', '');
    });
    this.emailInputListenerAttached = true;
  };

  private setEmailInputErrorState = (show: boolean) => {
    const form = document.querySelector('[data-container="otp-generate-form"]');
    const formGroup = form?.querySelector('[data-container="form-email"]');
    if (formGroup) {
      formGroup.classList.toggle('error', show);
    }
  };

  private getValidationSummary = (formContainer: string): Element | null => {
    return document.querySelector(`[data-container="${formContainer}"] [data-container="validation-summary"]`);
  };

  private setValidationSummary = (formContainer: string, message: string) => {
    const validationSummary = this.getValidationSummary(formContainer);
    if (validationSummary) {
      validationSummary.textContent = message;
    }
  };

  private handleFormError = (e: Event) => {
    const target = e.target as HTMLElement;
    if (target?.dataset?.container === 'otp-generate-form') {
      setTimeout(() => this.setEmailInputErrorState(true), 0);
      if (this.isResend) {
        this.isResend = false;
        this.handleResendError();
      }
    } else if (target?.dataset?.container === 'otp-validate-form') {
      this.overrideValidationMessage('otp-validate-form', 'otp_validate_error');
      OtpDigitInputs.Instance?.setErrorState();
    }
  };

  private overrideValidationMessage = async (formContainer: string, translationKey: TranslationKeys) => {
    const translator = TranslatorService.getInstance();
    const message = await translator.translate(translationKey);
    if (!message) return;

    this.setValidationSummary(formContainer, message);
  };

  // Auto-opens the OTP login flow when the URL contains ?otp=true,
  // e.g. from a Klaviyo email link that directs the customer to log in via OTP.
  private autoOpenOtpFlowFromUrl = () => {
    if (new URLSearchParams(window.location.search).get('otp') !== 'true') return;

    const observer = new MutationObserver((_mutations, obs) => {
      const panel = this.getEmailEntryPanel();
      if (panel) {
        obs.disconnect();
        requestAnimationFrame(() => this.resumeOrStartOtpFlow());
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };
}
